<template>
  <div>
    <resize-sensor @resize="onresize"></resize-sensor>
    <div :style="[colorStyles, { '--months-margin': monthsMarginComputed }]" class="wrapper">
      <template v-if="!multiLine">
        <div class="button__wrapper">
          <div v-show="canScrollLeft"
               :class="{ 'button--image': imageButtonPrev }"
               :style="imageButtonStyle(imageButtonPrev)"
               class="button button--previous"
               @click="scrollLeft">
            <template v-if="imageButtonPrev === null">
              &lt;
            </template>
          </div>
        </div>
        <div class="button__wrapper button__wrapper--right">
          <div v-show="canScrollRight"
               :class="{ 'button--image': imageButtonNext }"
               :style="imageButtonStyle(imageButtonNext)"
               class="button button--next"
               @click="scrollRight">
            <template v-if="imageButtonNext === null">
              &gt;
            </template>
          </div>
        </div>
      </template>
      <div ref="container" :style="{ '--visible-months': responsiveMonths }" class="container">
        <div v-if="loaded" :class="[{'calendar--border': dayBorder === false}, {'calendar--multi-line': multiLine}]" class="calendar">
          <month v-for="month in visibleMonths"
                 :key="month.toISOString()"
                 :label="month | toLabel"
                 :weekdays="weekdays"
                 :first-day-of-week="firstDayOfWeek"
                 :first-day-of-month="month"
                 :selected-dates="selectedDates"
                 :min-date="minDate"
                 :max-date="maxDate"
                 :date-colors="dateColors"
                 :season-dates="seasonDates"
                 @selectdate="onSelectDate($event, month)"
                 @hoverdate="onHoverDate($event, month)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import fecha from 'fecha';
import ResizeSensor from 'vue-resize-sensor';
import { isNil, isNumber, isNaN } from 'lodash';
import Month from './Month';
import HotelmeisterMixin from '../../../mixins/HotelmeisterMixin';

export default {
  name: 'AvailabilityCalendar',
  components: { Month, ResizeSensor },

  filters: {
    toLabel: function (date) {
      return fecha.format(date, 'MMMM YYYY', fecha.i18n);
    },
  },

  mixins: [HotelmeisterMixin],

  props: {
    locale: {
      type: String,
      default: 'de',
    },
    months: {
      type: Number,
      default: 3,
    },
    maxMonths: {
      type: Number,
      default: 36,
    },
    unavailableColor: {
      type: String,
      default: '#eccccd',
    },
    availableColor: {
      type: String,
      default: '#d2e6c4',
    },
    borderColor: {
      type: String,
      default: null,
    },
    dayBorder: {
      type: Boolean,
      default: true,
    },
    imageButtonNext: {
      type: String,
      default: null,
    },
    imageButtonPrev: {
      type: String,
      default: null,
    },
    monthsMargin: {
      type: Number,
      default: 70,
    },
    multiLine: {
      type: Boolean,
      default: true,
    },
    dateColors: {
      type: Object,
      default: () => ({}),
    },
    seasonDates: {
      type: Object,
      default: () => ({}),
    },
  },

  data: function () {
    return {
      today: new Date(),
      mounted: false,
      datesLoaded: false,
      disabledDates: [],
      arrivalOnlyDates: [],
      departureOnlyDates: [],
      weekdays: [],
      firstDayOfWeek: 'mo',
      visibleMonth: 0,
      responsiveMonths: this.months,
      selection: {
        startDate: null,
        endDate: null,
        hoverDate: null,
      },
      minDate: null,
      maxDate: null,
    };
  },

  i18n: {
    de: {
      dayNamesShort: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
      firstDayOfWeek: 'mo',
      monthNames: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
    },
    en: {
      dayNamesShort: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
      firstDayOfWeek: 'su',
      monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    },
  },

  computed: {
    effectiveLocale() {
      if (isNil(this.locale) || this.locale.length < 2) return 'de';
      switch (this.locale.substring(0, 2).toLowerCase()) {
        case 'de':
        case 'en':
          return this.locale.toLowerCase();
        default:
          return 'en';
      }
    },
    firstDayOfMinMonth() {
      const minDateMoment = this.$moment(this.minDate);
      return !this.mounted ? null : new Date(minDateMoment.year(), minDateMoment.month(), 1);
    },
    visibleMonths: function () {
      const ret = [];
      ret.push(this.firstDayOfMinMonth);
      for (let $i = 1; $i < Math.min(this.maxMonths, 40); $i++) {
        ret.push(new Date(this.firstDayOfMinMonth.getYear() + 1900, this.firstDayOfMinMonth.getMonth() + $i, 1));
      }
      return ret;
    },
    loaded: function () {
      return this.mounted;
    },
    colorStyles: function () {
      return {
        '--red': this.unavailableColor,
        '--green': this.availableColor,
        '--border-color': this.borderColor,
      };
    },
    canScrollRight: function () {
      return this.visibleMonth !== Math.min(this.maxMonths, 40) - this.responsiveMonths;
    },
    canScrollLeft: function () {
      return this.visibleMonth !== 0;
    },
    monthsMarginComputed: function () {
      return `${this.monthsMargin}px`;
    },
    selectedDates: function () {
      if (isNil(this.selection.startDate) || (isNil(this.selection.endDate) && isNil(this.selection.hoverDate))) {
        return [];
      }
      let startDate = this.$moment(this.selection.startDate, 'YYYYMMDD');
      let endDate = isNil(this.selection.hoverDate) ? this.$moment(this.selection.endDate, 'YYYYMMDD') : this.$moment(this.selection.hoverDate, 'YYYYMMDD');

      if (startDate.isAfter(endDate)) {
        const tmp = startDate;
        startDate = endDate;
        endDate = tmp;
      }

      const ret = [];

      let tmp = startDate;

      for (let i = 0; !tmp.isAfter(endDate); i++) {
        ret.push(tmp.format('YYYY-MM-DD'));
        tmp = tmp.add(1, 'days');
      }

      return ret;
    },
  },

  watch: {
    locale: function () {
      this.updateLocale();
    },
    months: function () {
      this.responsiveMonths = this.months;
    },
  },

  created: function () {
    this.weekdays = this.$options.i18n[this.effectiveLocale].dayNamesShort;
    this.firstDayOfWeek = this.$options.i18n[this.effectiveLocale].firstDayOfWeek;
    this.minDate = this.$moment().add(-1, 'days').format('YYYY-MM-DD');
    this.maxDate = this.$moment().add(this.hotelSettings.details.futureDates, 'days').format('YYYY-MM-DD');
    this.updateLocale();
    this.updateDates();
    this.$serviceBus.$on('shortcut.esc', () => {
      this.clearSelection();
    });
  },

  mounted: function () {
    this.mounted = true;
  },

  methods: {
    clearSelection() {
      this.selection.startDate = null;
      this.selection.endDate = null;
      this.selection.hoverDate = null;
      this.$emit('clear', this.selection);
    },
    onHoverDate(day, month) {
      if (!isNumber(parseInt(day, 10)) || isNaN(parseInt(day, 10))) return;
      const date = new Date(month.getYear() + 1900, month.getMonth(), day);

      if (!isNil(this.selection.startDate) && isNil(this.selection.endDate)) {
        this.selection.hoverDate = fecha.format(date, 'YYYYMMDD');
      } else {
        this.selection.hoverDate = null;
      }
    },
    onSelectDate(day, month) {
      if (!isNumber(parseInt(day, 10)) || isNaN(parseInt(day, 10))) return;
      const date = new Date(month.getYear() + 1900, month.getMonth(), day);

      if (isNil(this.selection.startDate) || !isNil(this.selection.endDate)) {
        this.selection.startDate = fecha.format(date, 'YYYYMMDD');
        this.selection.endDate = null;
        this.selection.hoverDate = fecha.format(date, 'YYYYMMDD');
      } else {
        this.selection.endDate = fecha.format(date, 'YYYYMMDD');
        this.selection.hoverDate = null;

        if (this.selection.endDate < this.selection.startDate) {
          const tmp = this.selection.startDate;
          this.selection.startDate = this.selection.endDate;
          this.selection.endDate = tmp;
        }

        this.$emit('select', this.selection);
      }
    },
    imageButtonStyle(imageButtonPath) {
      return imageButtonPath ? `background-image: url('${imageButtonPath}')` : null;
    },
    onresize(event) {
      const availableWidth = event.width;

      for (let i = this.months; i >= 1; i--) {
        if (availableWidth >= this.visibleSize(i)) {
          this.responsiveMonths = i;
          return;
        }
      }

      this.responsiveMonths = 1;
    },
    visibleSize(months) {
      return (247 + 6 * 7) * months + this.monthsMargin * (months - 1) + 2;
    },
    scrollLeft() {
      this.visibleMonth = Math.max(0, this.visibleMonth - 1);
      this.updateScroll();
    },
    scrollRight() {
      this.visibleMonth = Math.min(Math.min(this.maxMonths, 40) - this.responsiveMonths, this.visibleMonth + 1);
      this.updateScroll();
    },
    updateScroll() {
      const width = this.$refs.container.scrollWidth;
      const offset = 247 + this.monthsMargin + 6 * 7;
      const newScroll = Math.min(width, offset * this.visibleMonth);

      if (typeof this.$refs.container.scrollTo === 'undefined') {
        this.$refs.container.scrollLeft = newScroll;
      } else {
        this.$refs.container.scrollTo({ top: 0, left: newScroll, behavior: 'smooth' });
      }
    },
    updateLocale() {
      fecha.i18n = {
        ...this.$options.i18n[this.effectiveLocale],
      };
      this.weekdays = this.$options.i18n[this.effectiveLocale].dayNamesShort;
      this.firstDayOfWeek = this.$options.i18n[this.effectiveLocale].firstDayOfWeek;
    },
    isNil(val) {
      return (val === null || typeof val === 'undefined');
    },
    updateDates() {
    },
  },
};
</script>

<style lang="scss">
  @import '~styles/import';

  @mixin first-day-of-week() {
    $weekdays: 'mo','tu','we','th','fr','sa','su';

    @each $weekday in $weekdays {
      $offset: 7 - index($weekdays, $weekday);

      @if $offset >= 7 {
        $offset: $offset - 7;
      }

      &.first-day-of-week {
        &--#{$weekday} {

          @for $i from 1 through length($weekdays) {
            $itWeekday: nth($weekdays, $i);
            $start: $i + $offset + 1;

            @if $start > 7 {
              $start: $start - 7;
            }

            .weekday:nth-of-type(#{$i}) {
              grid-column-start: $start;
            }
          }

          .day {
            @for $i from 1 through length($weekdays) {
              $itWeekday: nth($weekdays, $i);
              $start: $i + $offset + 1;

              @if $start > 7 {
                $start: $start - 7;
              }

              &.#{$itWeekday} {
                grid-column-start: $start;
              }
            }
          }
        }
      }
    }
  }

  .wrapper {
    display: none;
    --border-color: #cacccd;

    @supports(display: grid) {
      display: grid;
      grid-template-columns: max-content max-content;
      user-select: none;

      .button {
        padding: 10px 14px;
        font-size: 120%;
        line-height: 90%;
        border: 1px solid lightgray;
        border: 1px solid var(--border-color);
        text-align: center;
        font-weight: bold;
        cursor: pointer;
        transition: background-color 0.3s ease, box-shadow 0.1s ease;

        &:hover {
          background-color: #efefef;
        }

        &:active {
          box-shadow: inset 0px 0px 10px 0px #ccc;
        }

        &__wrapper {
          display: grid;
          grid-template-columns: min-content;

          &--right {
            justify-content: right;
          }
        }

        &--next {
          float: right;
        }

        &--image {
          border: none;
          padding: 0;
          width: 48px;
          height: 48px;
          background-position: center center;
          background-size: contain;
          background-repeat: no-repeat;

          &:hover {
            background-color: transparent;
          }

          &:active {
            box-shadow: none;
          }
        }
      }
    }
  }

  .container {
    grid-column-start: span 2;
    max-width: calc((247px + 6 * 7px) * var(--visible-months) + var(--months-margin) * (var(--visible-months) - 1) + 2px);
    overflow: hidden;
    user-select: none;
  }

  .calendar {
    display: grid;
    grid-template-columns: repeat(200, calc(247px + 6 * 7px));
    grid-gap: var(--months-margin);
    grid-row-gap: 35px;

    &--multi-line {
      grid-template-columns: repeat(var(--visible-months), calc(247px + 6 * 7px));
    }

    &--border {
        .month {
            .day {
                box-shadow: 0 0 0 1px white;
                box-shadow: 0 0 0 1px var(--border-color);
                &.disabled {
                    box-shadow: 0 0 0 1px var(--red);
                }
                &.departure-only {
                    &:before {
                        box-shadow: 1px 1px 0 0px var(--red);
                    }
                }
                &.arrival-only {
                    &:before {
                        box-shadow: -1px -1px 0 0px var(--red);
                    }
                }
            }
        }
    }

    .month {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      max-width: calc(247px + 6 * 7px);
      font-family: sans-serif;
      box-sizing: border-box;
      grid-gap: 1px;
      grid-auto-rows: 35px;
      grid-template-rows: 35px 50px repeat(6, 35px);

      grid-row-gap: 7px;
      grid-column-gap: 7px;

      padding: 1px;

      .month-name {
        grid-column-start: span 7;
        font-weight: bold;
        text-align: center;
        line-height: 35px;
        color: darkgray;
        text-transform: uppercase;
        font-size: 85%;
      }

      .weekdays {
        display: grid;
        grid-column-start: 1;
        grid-column-end: span 7;
        grid-template-columns: repeat(7, 1fr);
        margin-bottom: 5px;
        height: 35px;
      }

      .weekday {
        line-height: 25px;
        grid-row-start: 2;
        text-align: center;
        text-transform: uppercase;
        border-top: 1px solid white;
        border-top: 1px solid var(--border-color);
        border-bottom: 1px solid white;
        border-bottom: 1px solid var(--border-color);
        margin-left: -1px;
        margin-right: -1px;
        margin-bottom: -1px;
        font-size: 85%;
        padding: 4px;
      }

      .day {
        text-align: center;
        line-height: 35px;
        background-color: lightgreen;
        background-color: var(--green);
        position: relative;
        border: 1px solid var(--border-color);
        border-radius: 3px;
        cursor: pointer;

        span {
          z-index: 999;
          display: block;
          position: relative;
        }

        &.selected {
          background-color: #CCCDCF;
          position: relative;

          &:before {
            display: block;
            content: ' ';
            position: absolute;
            left: -4.5px;
            top: -1px;
            bottom: -1px;
            right: -4.5px;
            background-color: #CCCDCF;
            border-top: 1px solid var(--border-color);
            border-bottom: 1px solid var(--border-color);
          }

          &--start, &--end {
            background-color: #383F45;
            border: 1px solid #383F45;
            color: white;

            &:before {
              display: block;
              content: ' ';
              position: absolute;
              left: -4.5px;
              top: -1px;
              bottom: -1px;
              right: -4.5px;
              background-color: #383F45;
              border: 1px solid #383F45;
            }
          }

          &--start {
            &:before {
              border-top-left-radius: 3px;
              border-bottom-left-radius: 3px;
              left: 0px;
            }
          }

          &--end {
            &:before {
              border-top-right-radius: 3px;
              border-bottom-right-radius: 3px;
              right: 0px;
            }
          }
        }

        &.past {
          border: none;
          color: $middlegray;
          pointer-events: none;
        }

        &.disabled {
          background-color: lightcoral;
          background-color: var(--red);
        }
        &.departure-only {
          &:before {
            content: ' ';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(to right bottom, transparent 50%, lightcoral 50%);
            background: linear-gradient(to right bottom, transparent 50%, var(--red) 50%);
          }
        }
        &.arrival-only {
          &:before {
            content: ' ';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(to left top, transparent 50%, lightcoral 50%);
            background: linear-gradient(to left top, transparent 50%, var(--red) 50%);
          }
        }
      }

      @include first-day-of-week();
    }

  }
</style>



// WEBPACK FOOTER //
// src/components/lib/calendar/SeasonCalendar.vue